import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Workflow, Plus, Play, Pause, Edit, Trash2, Users, Zap,
  Tag, Calendar, MessageSquare, Mail, Clock, ArrowRight,
  ChevronDown, ChevronRight, Settings, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const triggerTypes = [
  { id: 'lead_created', label: 'Lead Created', icon: Users, description: 'When a new lead is added' },
  { id: 'tag_added', label: 'Tag Added', icon: Tag, description: 'When a specific tag is added to a lead' },
  { id: 'tag_removed', label: 'Tag Removed', icon: Tag, description: 'When a specific tag is removed' },
  { id: 'appointment_booked', label: 'Appointment Booked', icon: Calendar, description: 'When an appointment is scheduled' },
  { id: 'appointment_outcome', label: 'Appointment Outcome', icon: Calendar, description: 'When appointment outcome is recorded' },
  { id: 'form_submitted', label: 'Form Submitted', icon: Zap, description: 'When a form/survey is submitted' },
  { id: 'webhook', label: 'Webhook', icon: Zap, description: 'External trigger via webhook' },
  { id: 'manual', label: 'Manual', icon: Play, description: 'Manually enroll leads' },
];

const actionTypes = [
  { id: 'add_tag', label: 'Add Tag', icon: Tag, description: 'Add a tag to the lead' },
  { id: 'remove_tag', label: 'Remove Tag', icon: Tag, description: 'Remove a tag from the lead' },
  { id: 'send_sms', label: 'Send SMS', icon: MessageSquare, description: 'Send an SMS message' },
  { id: 'send_email', label: 'Send Email', icon: Mail, description: 'Send an email' },
  { id: 'move_stage', label: 'Move Pipeline Stage', icon: ArrowRight, description: 'Move lead to a different stage' },
  { id: 'add_to_followup', label: 'Add to Follow-up', icon: Calendar, description: 'Schedule a follow-up callback' },
  { id: 'wait', label: 'Wait/Delay', icon: Clock, description: 'Wait for a specified time' },
];

const WorkflowsPage = () => {
  const { user, selectedBusiness } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [tags, setTags] = useState([]);
  const [triggerOptions, setTriggerOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [tagForm, setTagForm] = useState({ name: '', color: '#3B82F6' });
  const [creatingTag, setCreatingTag] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: { type: '', config: {} },
    steps: []
  });

  const businessId = selectedBusiness?.business_id || user?.business_id;

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const businessParam = businessId ? `?business_id=${businessId}` : '';

      const [workflowsRes, tagsRes, triggerOptionsRes] = await Promise.all([
        fetch(`${API_URL}/api/workflows${businessParam}`, { headers }),
        fetch(`${API_URL}/api/tags${businessParam}`, { headers }),
        fetch(`${API_URL}/api/workflows/trigger-options${businessParam}`, { headers })
      ]);

      if (workflowsRes.ok) setWorkflows(await workflowsRes.json());
      if (tagsRes.ok) setTags(await tagsRes.json());
      if (triggerOptionsRes.ok) {
        const data = await triggerOptionsRes.json();
        setTriggerOptions(data.form_sources || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (workflowId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/workflows/${workflowId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(currentStatus ? 'Workflow paused' : 'Workflow activated');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update workflow');
    }
  };

  const handleDelete = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/workflows/${workflowId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Workflow deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.trigger.type) {
      toast.error('Please fill in workflow name and trigger');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const businessParam = businessId ? `?business_id=${businessId}` : '';
      const url = editingWorkflow 
        ? `${API_URL}/api/workflows/${editingWorkflow.workflow_id}`
        : `${API_URL}/api/workflows${businessParam}`;
      
      const response = await fetch(url, {
        method: editingWorkflow ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingWorkflow ? 'Workflow updated!' : 'Workflow created!');
        setIsModalOpen(false);
        setEditingWorkflow(null);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save workflow');
      }
    } catch (error) {
      toast.error('Failed to save workflow');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger: { type: '', config: {} },
      steps: []
    });
  };

  const openCreateModal = () => {
    setEditingWorkflow(null);
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      name: workflow.name,
      description: workflow.description || '',
      trigger: workflow.trigger,
      steps: workflow.steps
    });
    setIsModalOpen(true);
  };

  const handleCreateTag = async () => {
    if (!tagForm.name.trim()) {
      toast.error('Enter a tag name');
      return;
    }

    try {
      setCreatingTag(true);
      const token = localStorage.getItem('token');
      const businessParam = businessId ? `?business_id=${businessId}` : '';
      const response = await fetch(`${API_URL}/api/tags${businessParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: tagForm.name.trim(),
          color: tagForm.color,
          description: ''
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to create tag');
        return;
      }

      const createdTag = await response.json();
      setTags((prev) => [...prev, createdTag]);
      setTagForm({ name: '', color: '#3B82F6' });
      toast.success('Tag created');
    } catch (error) {
      toast.error('Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const addStep = (type) => {
    const newStep = {
      step_id: `step_${Date.now()}`,
      type: 'action',
      action_type: type,
      config: {},
      next_step_id: null
    };
    setFormData({
      ...formData,
      steps: [...formData.steps, newStep]
    });
  };

  const removeStep = (stepId) => {
    setFormData({
      ...formData,
      steps: formData.steps.filter(s => s.step_id !== stepId)
    });
  };

  const updateStepConfig = (stepId, config) => {
    setFormData({
      ...formData,
      steps: formData.steps.map(s => 
        s.step_id === stepId ? { ...s, config: { ...s.config, ...config } } : s
      )
    });
  };

  const selectedTriggerSource = triggerOptions.find((option) => option.source === formData.trigger.config.source);
  const selectedTriggerForms = selectedTriggerSource?.forms || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="workflows-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Workflows</h1>
          <p className="text-sm sm:text-base text-slate-500">Automate your lead follow-up process</p>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 h-11"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Workflow
        </Button>
      </div>

      {/* Workflow Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Templates</CardTitle>
          <CardDescription>Start with a pre-built workflow template</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col items-start text-left"
              onClick={() => {
                setFormData({
                  name: 'Not Interested Follow-up',
                  description: 'Re-engage leads marked as not interested',
                  trigger: { type: 'tag_added', config: { tag_name: 'not_interested' } },
                  steps: [
                    { step_id: 'step_1', type: 'action', action_type: 'wait', config: { days: 7 } },
                    { step_id: 'step_2', type: 'action', action_type: 'send_sms', config: { message: 'Hi {name}, just checking in...' } }
                  ]
                });
                setIsModalOpen(true);
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Tag className="w-4 h-4 text-red-500" />
                <span className="font-medium">Not Interested Nurture</span>
              </div>
              <p className="text-xs text-slate-500">Auto follow-up after 7 days</p>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col items-start text-left"
              onClick={() => {
                setFormData({
                  name: 'New Lead Welcome',
                  description: 'Send welcome message to new leads',
                  trigger: { type: 'lead_created', config: {} },
                  steps: [
                    { step_id: 'step_1', type: 'action', action_type: 'send_sms', config: { message: 'Hi {name}, thanks for your interest!' } },
                    { step_id: 'step_2', type: 'action', action_type: 'add_tag', config: { tag_id: '' } }
                  ]
                });
                setIsModalOpen(true);
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">New Lead Welcome</span>
              </div>
              <p className="text-xs text-slate-500">Send immediate welcome</p>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col items-start text-left"
              onClick={() => {
                setFormData({
                  name: 'Appointment Reminder',
                  description: 'Remind leads about upcoming appointments',
                  trigger: { type: 'appointment_booked', config: {} },
                  steps: [
                    { step_id: 'step_1', type: 'action', action_type: 'send_sms', config: { message: 'Reminder: Your appointment is tomorrow!' } }
                  ]
                });
                setIsModalOpen(true);
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-blue-500" />
                <span className="font-medium">Appointment Reminder</span>
              </div>
              <p className="text-xs text-slate-500">Send booking confirmation</p>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Workflow List */}
      {workflows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Workflow className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No workflows yet</h3>
            <p className="text-slate-500 mt-2">Create your first workflow to automate lead follow-ups</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {workflows.map((workflow) => {
            const triggerInfo = triggerTypes.find(t => t.id === workflow.trigger?.type);
            const TriggerIcon = triggerInfo?.icon || Zap;
            
            return (
              <Card key={workflow.workflow_id} className={workflow.is_active ? 'border-emerald-200' : ''}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${workflow.is_active ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                        <TriggerIcon className={`w-5 h-5 ${workflow.is_active ? 'text-emerald-600' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{workflow.name}</h3>
                          <Badge className={workflow.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                            {workflow.is_active ? 'Active' : 'Paused'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{workflow.description || triggerInfo?.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {workflow.enrollments_count} active
                          </span>
                          <span className="flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {workflow.steps?.length || 0} steps
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={workflow.is_active}
                        onCheckedChange={() => handleToggle(workflow.workflow_id, workflow.is_active)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(workflow)}
                        className="h-9 w-9 p-0"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(workflow.workflow_id)}
                        className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Workflow Name</Label>
                <Input
                  placeholder="e.g., New Lead Follow-up"
                  className="mt-1.5"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  placeholder="What does this workflow do?"
                  className="mt-1.5"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            {/* Trigger */}
            <div>
              <Label className="text-base font-semibold">Trigger</Label>
              <p className="text-sm text-slate-500 mb-3">What starts this workflow?</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {triggerTypes.map((trigger) => {
                  const Icon = trigger.icon;
                  const isSelected = formData.trigger.type === trigger.id;
                  return (
                    <button
                      key={trigger.id}
                      type="button"
                      onClick={() => setFormData({ 
                        ...formData, 
                        trigger: { type: trigger.id, config: {} } 
                      })}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        isSelected 
                          ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-200' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-cyan-600' : 'text-slate-500'}`} />
                      <p className="font-medium text-sm">{trigger.label}</p>
                    </button>
                  );
                })}
              </div>

              {/* Trigger Config */}
              {formData.trigger.type === 'tag_added' && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <Label>Which tag triggers this workflow?</Label>
                  <Select
                    value={formData.trigger.config.tag_id || ''}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      trigger: { ...formData.trigger, config: { tag_id: v } }
                    })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select a tag..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tags.map(tag => (
                        <SelectItem key={tag.tag_id} value={tag.tag_id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                            {tag.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tags.length === 0 && (
                    <div className="mt-3 space-y-2 rounded-lg border border-dashed border-slate-300 p-3">
                      <p className="text-sm text-slate-500">No tags yet. Create one here.</p>
                      <div className="flex gap-2">
                        <Input
                          value={tagForm.name}
                          placeholder="Cold Lead"
                          onChange={(e) => setTagForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                        <Input
                          type="color"
                          value={tagForm.color}
                          className="h-10 w-14 p-1"
                          onChange={(e) => setTagForm((prev) => ({ ...prev, color: e.target.value }))}
                        />
                        <Button type="button" variant="outline" onClick={handleCreateTag} disabled={creatingTag}>
                          {creatingTag ? 'Creating...' : 'Create Tag'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {formData.trigger.type === 'tag_removed' && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <Label>Which removed tag triggers this workflow?</Label>
                  <Select
                    value={formData.trigger.config.tag_id || ''}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      trigger: { ...formData.trigger, config: { tag_id: v } }
                    })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select a tag..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tags.map(tag => (
                        <SelectItem key={tag.tag_id} value={tag.tag_id}>{tag.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.trigger.type === 'appointment_outcome' && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <Label>Which outcome triggers this workflow?</Label>
                  <Select
                    value={formData.trigger.config.outcome || ''}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      trigger: { ...formData.trigger, config: { outcome: v } }
                    })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select outcome..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="closed">Closed Won</SelectItem>
                      <SelectItem value="follow_up">Follow-up Needed</SelectItem>
                      <SelectItem value="not_interested">Not Interested</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.trigger.type === 'form_submitted' && (
                <div className="mt-4 space-y-4 rounded-lg bg-slate-50 p-4">
                  <div>
                    <Label>Which source triggers this workflow?</Label>
                    <Select
                      value={formData.trigger.config.source || ''}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        trigger: {
                          ...formData.trigger,
                          config: { source: value }
                        }
                      })}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select source..." />
                      </SelectTrigger>
                      <SelectContent>
                        {triggerOptions.map((option) => (
                          <SelectItem key={option.source} value={option.source}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedTriggerForms.length > 0 && (
                    <div>
                      <Label>Select a form</Label>
                      <Select
                        value={formData.trigger.config.form_id || ''}
                        onValueChange={(value) => {
                          const selectedForm = selectedTriggerForms.find((form) => form.id === value);
                          setFormData({
                            ...formData,
                            trigger: {
                              ...formData.trigger,
                              config: {
                                ...formData.trigger.config,
                                form_id: value,
                                form_name: selectedForm?.name || '',
                              }
                            }
                          });
                        }}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Select form..." />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedTriggerForms.map((form) => (
                            <SelectItem key={form.id} value={form.id}>
                              {form.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {['website', 'google'].includes(formData.trigger.config.source) && (
                    <div>
                      <Label>Form name</Label>
                      <Input
                        className="mt-1.5"
                        placeholder={formData.trigger.config.source === 'google' ? 'Google Form Name' : 'Website Form Name'}
                        value={formData.trigger.config.form_name || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          trigger: {
                            ...formData.trigger,
                            config: {
                              ...formData.trigger.config,
                              form_name: e.target.value,
                            }
                          }
                        })}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Steps */}
            <div>
              <Label className="text-base font-semibold">Actions</Label>
              <p className="text-sm text-slate-500 mb-3">What happens when triggered?</p>
              
              {/* Existing Steps */}
              {formData.steps.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.steps.map((step, index) => {
                    const actionInfo = actionTypes.find(a => a.id === step.action_type);
                    const ActionIcon = actionInfo?.icon || Zap;
                    
                    return (
                      <div key={step.step_id} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-center w-6 h-6 bg-cyan-100 rounded-full text-xs font-bold text-cyan-700">
                          {index + 1}
                        </div>
                        <ActionIcon className="w-4 h-4 text-slate-500" />
                        <span className="flex-1 font-medium text-sm">{actionInfo?.label}</span>
                        
                        {step.action_type === 'wait' && (
                          <Input
                            type="number"
                            className="w-20 h-8"
                            placeholder="Days"
                            value={step.config.days || ''}
                            onChange={(e) => updateStepConfig(step.step_id, { days: parseInt(e.target.value) })}
                          />
                        )}
                        
                        {step.action_type === 'add_tag' && (
                          <Select
                            value={step.config.tag_id || ''}
                            onValueChange={(v) => updateStepConfig(step.step_id, { tag_id: v })}
                          >
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue placeholder="Tag" />
                            </SelectTrigger>
                            <SelectContent>
                              {tags.map(tag => (
                                <SelectItem key={tag.tag_id} value={tag.tag_id}>{tag.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        {step.action_type === 'remove_tag' && (
                          <Select
                            value={step.config.tag_id || ''}
                            onValueChange={(v) => updateStepConfig(step.step_id, { tag_id: v })}
                          >
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue placeholder="Tag" />
                            </SelectTrigger>
                            <SelectContent>
                              {tags.map(tag => (
                                <SelectItem key={tag.tag_id} value={tag.tag_id}>{tag.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStep(step.step_id)}
                          className="h-8 w-8 p-0 text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Step Buttons */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {actionTypes.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Button
                      key={action.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addStep(action.id)}
                      className="h-auto py-2 justify-start"
                    >
                      <Icon className="w-4 h-4 mr-2 text-slate-500" />
                      {action.label}
                    </Button>
                  );
                })}
              </div>
              {tags.length === 0 && (
                <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-3">
                  <p className="mb-3 text-sm text-slate-500">No tags yet. Default tags are created per business, or add one now.</p>
                  <div className="flex gap-2">
                    <Input
                      value={tagForm.name}
                      placeholder="Warm Lead"
                      onChange={(e) => setTagForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                    <Input
                      type="color"
                      value={tagForm.color}
                      className="h-10 w-14 p-1"
                      onChange={(e) => setTagForm((prev) => ({ ...prev, color: e.target.value }))}
                    />
                    <Button type="button" variant="outline" onClick={handleCreateTag} disabled={creatingTag}>
                      {creatingTag ? 'Creating...' : 'Create Tag'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-cyan-500 hover:bg-cyan-600">
              {editingWorkflow ? 'Update' : 'Create'} Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowsPage;
